import { useState } from "react";

import { Provider } from "~/services";
import { TimeSlot } from "~/shared.types";
import { customIcons, icons } from "~/ui/common/Icons";
import { formatDateKey, tw } from "~/utils";
import { formatProviderLabel } from "../utils";
import { ProviderProfileModal } from "./ProviderProfileModal";
import { TimeSlotChip } from "./TimeSlotChip";

export type ProviderScheduleOptions = {
  variant?: "default" | "compact";
};

type ProviderScheduleProps = ProviderScheduleOptions & {
  provider: Provider;
  selectedDate: Date;
  selectedTimeSlot?: TimeSlot;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  className?: string;
};

export const ProviderSchedule = ({
  provider,
  selectedDate,
  selectedTimeSlot,
  onTimeSlotSelect,
  className,
  variant = "default",
}: ProviderScheduleProps) => {
  const isCompact = variant === "compact";
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showAllSlots, setShowAllSlots] = useState(false);

  const handleViewProfileClick = () => {
    setIsProfileOpen(true);
  };

  const SLOTS_LIMIT = 6;

  const providerLabel = formatProviderLabel(provider.name, provider.title);

  // Get slots for the selected date
  const dateString = formatDateKey(selectedDate);

  const allSlots = provider.slots[dateString] || [];
  const displayedSlots = showAllSlots
    ? allSlots
    : allSlots.slice(0, SLOTS_LIMIT);

  const hasMoreSlots = allSlots.length > SLOTS_LIMIT;

  const handleTimeSlotClick = (timeSlot: TimeSlot) => {
    onTimeSlotSelect(timeSlot);
  };

  return (
    <section className={tw("space-y-2", className)}>
      <section className="bg-light-lavender-gray/50 space-y-2 rounded-xl p-2 px-3 transition-colors">
        <div className="flex items-center gap-3">
          <div
            className={tw(
              "flex size-16 items-center justify-center overflow-hidden rounded-full bg-gray-200",
              isCompact && "size-10 shrink-0",
            )}
          >
            {provider.image ? (
              <img
                src={provider.image}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <icons.outline.UserIcon
                className={tw("size-8 text-gray-500", isCompact && "size-5")}
              />
            )}
          </div>
          <div
            className={tw(
              "text-left font-sans",
              isCompact &&
                "flex min-w-0 flex-1 items-center justify-between gap-3",
            )}
          >
            <h2 className="text-indigo text-base font-medium">
              {providerLabel}
            </h2>
            <button
              type="button"
              onClick={handleViewProfileClick}
              className={tw(
                "text-indigo text-sm font-normal underline",
                isCompact && "shrink-0",
              )}
            >
              View profile
            </button>
          </div>
        </div>
      </section>

      <ProviderProfileModal
        provider={provider}
        show={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />

      {/* Time slots */}
      <div className="border-t border-gray-100 px-2 pb-4">
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {displayedSlots.map((timeSlot) => (
            <TimeSlotChip
              key={timeSlot.id}
              slot={timeSlot}
              onSelect={handleTimeSlotClick}
              isSelected={selectedTimeSlot?.id === timeSlot.id}
              providerLabel={providerLabel}
            />
          ))}
        </div>

        {/* Show more/less button */}
        {hasMoreSlots && (
          <button
            onClick={() => setShowAllSlots(!showAllSlots)}
            className="text-violet hover:bg-indigo/5 mt-3 flex items-center gap-1 rounded-lg p-2 text-sm font-medium transition-colors"
          >
            {showAllSlots ? "Show less" : "More availability"}
            <customIcons.TriangleShape
              className={tw(
                "text-violet size-3 transition-transform",
                showAllSlots && "rotate-180",
              )}
            />
          </button>
        )}
      </div>
    </section>
  );
};
